import React, { useMemo } from 'react';
import { Mail, Server } from 'lucide-react';
import { RunStatePill } from '../../components/EtlPages';
import { MobileCard, MobileGroup, timeAgo } from '../../components/FileTracking/EtlMobileKit';

/** "[error stage] message" for the pill tooltip, as the desktop table shows it. */
export const lastRunError = (lastRun) => (lastRun?.lastErrorMessage
  ? `${lastRun.lastErrorStage ? `[${lastRun.lastErrorStage}] ` : ''}${lastRun.lastErrorMessage}`
  : undefined);

export function MethodTag({ method }) {
  const sftp = method === 'sftp';
  const Icon = sftp ? Server : Mail;
  return (
    <span className="ep-m-tag">
      <Icon size={11} aria-hidden="true" />
      <span>{sftp ? 'SFTP' : 'Email'}</span>
    </span>
  );
}

/**
 * Phone form of the Feeds table: feeds grouped under their property
 * ("PROPERTY NAME · CODE" + count), one card per feed — the feed name with
 * its last outcome, the arrival method and extraction as small tags, and how
 * long ago it last received a file. Tapping a card hands the row to `onOpen`
 * (FeedsTab opens the actions sheet).
 */
export default function FeedsCardList({ rows, locationName, extractionLabel, onOpen }) {
  const groups = useMemo(() => {
    const byHotel = new Map();
    for (const r of rows) {
      const key = String(r.hotelId);
      if (!byHotel.has(key)) byHotel.set(key, { hotelId: r.hotelId, hotelCode: r.hotelCode, name: locationName(r.hotelId), rows: [] });
      const g = byHotel.get(key);
      if (!g.hotelCode && r.hotelCode) g.hotelCode = r.hotelCode;
      g.rows.push(r);
    }
    return [...byHotel.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((g) => ({ ...g, rows: [...g.rows].sort((a, b) => String(a.fileType).localeCompare(String(b.fileType))) }));
  }, [rows, locationName]);

  return (
    <div className="ep-m" data-testid="feeds-card-list">
      {groups.map((g) => (
        <MobileGroup
          key={g.hotelId}
          label={g.hotelCode ? `${g.name} · ${g.hotelCode}` : g.name}
          count={g.rows.length}
        >
          {g.rows.map((r) => {
            const extraction = r.lastRun?.ocrService || r.ocrService;
            const extractionText = extraction ? extractionLabel(extraction) : null;
            return (
              <MobileCard
                key={`${r.hotelId}-${r.fileType}`}
                title={r.fileType}
                titleClassName="ep-cap"
                trailing={<RunStatePill status={r.lastRun?.status || 'never'} title={lastRunError(r.lastRun)} />}
                sub={(
                  <>
                    <MethodTag method={r.method} />
                    {extractionText ? <span className="ep-m-tag"><span>{extractionText}</span></span> : null}
                  </>
                )}
                subEnd={timeAgo(r.lastRun?.receivedAt) || 'Never'}
                onClick={() => onOpen(r)}
                ariaLabel={`${r.fileType} feed, ${g.name}`}
              />
            );
          })}
        </MobileGroup>
      ))}
    </div>
  );
}
